import type { NavigateFunction } from 'react-router';

import type { TFunction } from 'i18next';

import toast from '~/utils/toast';

/**
 * Must match INTERNAL_REDIRECT_PATH_REGEX in app/models/product.rb and
 * client/src/utils/internalRedirectPath.ts.
 */
const INTERNAL_REDIRECT_PATH_REGEX = /^\/(?![/\\]).*/;

export type ApplyPostPurchaseRedirectParams = {
  redirectUrl: string | null | undefined;
  navigate: NavigateFunction;
  t: TFunction;
};

/**
 * Navigates after purchase when a post-purchase URL is configured (thank-you modal, instant redemption, etc.).
 */
export function applyPostPurchaseRedirect({ redirectUrl, navigate, t }: ApplyPostPurchaseRedirectParams): void {
  const trimmed = redirectUrl?.trim();
  if (!trimmed) return;

  if (INTERNAL_REDIRECT_PATH_REGEX.test(trimmed)) {
    navigate(trimmed);
    return;
  }

  try {
    const url = new URL(trimmed);
    if (!['http:', 'https:'].includes(url.protocol)) {
      throw new Error('Invalid protocol');
    }

    if (url.origin === window.location.origin) {
      navigate(`${url.pathname}${url.search}${url.hash}` || '/');
    } else {
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    }
  } catch {
    toast.error(t('products.thank_you.redirect_failed', 'Invalid redirect URL'));
  }
}
